import { useEffect, useState } from "react";
import {
  createInvitation,
  createOpportunity,
  getAuditEvents,
  getInvitations,
  getReminderSettings,
  importOpportunitiesCsv,
  updateReminderSettings,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import type {
  AuditEvent,
  Invitation,
  ReminderSettings,
  ReminderWeekday,
} from "../types";

const reminderWeekdays: ReminderWeekday[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export function AdminPage() {
  const { session } = useAuth();
  const { locale, t } = useI18n();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [showOpportunityForm, setShowOpportunityForm] = useState(false);
  const [showInvitationForm, setShowInvitationForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [notice, setNotice] = useState("");
  const [reminderSettings, setReminderSettings] = useState<ReminderSettings>({
    weekday: "Monday",
    hour: 10,
    timezone: "Asia/Hong_Kong",
  });
  const [opportunity, setOpportunity] = useState({
    clientDisplayName: "",
    market: "",
    projectName: "",
    projectSummary: "",
    nextCheckInOn: "",
  });
  useEffect(() => {
    if (!session) return;
    void Promise.all([
      getInvitations(session.accessToken),
      getAuditEvents(session.accessToken),
      getReminderSettings(session.accessToken),
    ]).then(([invites, events, settings]) => {
      setInvitations(invites);
      setAuditEvents(events);
      setReminderSettings(settings);
    });
  }, [session]);

  async function uploadCsv(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!session || !file) return;
    const count = await importOpportunitiesCsv(session.accessToken, file);
    setNotice(`${count} ${t("opportunities")}`);
    event.target.value = "";
  }

  async function submitInvitation(event: React.FormEvent) {
    event.preventDefault();
    if (!session) return;
    const result = await createInvitation(session.accessToken, inviteEmail);
    setInvitations((items) => [result.invitation, ...items]);
    setInviteEmail("");
    setShowInvitationForm(false);
    await navigator.clipboard?.writeText(result.invitationLink);
    setNotice(t("invitationCopied"));
  }

  async function submitOpportunity(event: React.FormEvent) {
    event.preventDefault();
    if (!session) return;
    await createOpportunity(session.accessToken, opportunity);
    setShowOpportunityForm(false);
    setNotice(t("addOpportunity"));
  }

  async function submitReminderSettings(event: React.FormEvent) {
    event.preventDefault();
    if (!session) return;
    const saved = await updateReminderSettings(
      session.accessToken,
      reminderSettings,
    );
    setReminderSettings(saved);
    setNotice(t("scheduleSaved"));
  }

  const weekdayLabels: Record<ReminderWeekday, string> = {
    Monday: t("monday"),
    Tuesday: t("tuesday"),
    Wednesday: t("wednesday"),
    Thursday: t("thursday"),
    Friday: t("friday"),
    Saturday: t("saturday"),
    Sunday: t("sunday"),
  };
  return (
    <div className="page">
      <section className="page-heading admin-heading">
        <div>
          <span className="eyebrow">{t("roleAdmin")}</span>
          <h1>{t("admin")}</h1>
          <p>{t("adminIntro")}</p>
        </div>
        <div className="button-row">
          <label className="secondary-button file-button">
            {t("importCsv")}
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => void uploadCsv(event)}
            />
          </label>
          <button
            className="primary-button"
            type="button"
            onClick={() => setShowOpportunityForm(true)}
          >
            ＋ {t("addOpportunity")}
          </button>
        </div>
      </section>
      <section className="admin-grid">
        <article className="panel schedule-panel">
          <div className="section-heading">
            <h2>{t("reminderSchedule")}</h2>
            <span>{t("weekly")}</span>
          </div>
          <p>{t("scheduleIntro")}</p>
          <form
            className="schedule-form"
            onSubmit={(event) => void submitReminderSettings(event)}
          >
            <label>
              {t("reminderDay")}
              <select
                value={reminderSettings.weekday}
                onChange={(event) =>
                  setReminderSettings({
                    ...reminderSettings,
                    weekday: event.target.value as ReminderWeekday,
                  })
                }
              >
                {reminderWeekdays.map((weekday) => (
                  <option key={weekday} value={weekday}>
                    {weekdayLabels[weekday]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("reminderHour")}
              <select
                value={reminderSettings.hour}
                onChange={(event) =>
                  setReminderSettings({
                    ...reminderSettings,
                    hour: Number(event.target.value),
                  })
                }
              >
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </label>
            <div className="schedule-timezone">
              <span>{t("timezone")}</span>
              <strong>Asia/Hong_Kong</strong>
            </div>
            <button className="primary-button" type="submit">
              {t("saveSchedule")}
            </button>
          </form>
        </article>
        <article className="panel">
          <div className="section-heading">
            <h2>{t("invitations")}</h2>
            <button
              className="text-button"
              type="button"
              onClick={() => setShowInvitationForm((open) => !open)}
              aria-label={t("createInvite")}
            >
              ＋
            </button>
          </div>
          {showInvitationForm && (
            <form
              className="inline-form"
              onSubmit={(event) => void submitInvitation(event)}
            >
              <label>
                {t("email")}
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  required
                />
              </label>
              <button className="primary-button" type="submit">
                {t("createInvite")}
              </button>
            </form>
          )}
          {invitations.map((invite) => (
            <div className="list-row" key={invite.id}>
              <div>
                <strong>{invite.email}</strong>
                <span>{invite.supplierName}</span>
              </div>
              <div className="align-right">
                <span className="status status-pending">
                  {invite.status === "accepted"
                    ? t("accepted")
                    : invite.status === "revoked"
                      ? t("revoked")
                      : t("pending")}
                </span>
                <small>
                  {t("expires")}{" "}
                  {new Intl.DateTimeFormat(locale, {
                    month: "short",
                    day: "numeric",
                  }).format(new Date(invite.expiresAt))}
                </small>
              </div>
            </div>
          ))}
        </article>
        <article className="panel reminder-panel">
          <div className="section-heading">
            <h2>{t("reminderCopy")}</h2>
            <span>{t("whatsApp")}</span>
          </div>
          <p>{t("reminderWhatsappText", { count: 1 })}</p>
          <button
            className="secondary-button"
            type="button"
            onClick={() =>
              void navigator.clipboard?.writeText(
                t("reminderWhatsappText", { count: 1 }),
              )
            }
          >
            {t("copyWhatsApp")}
          </button>
          <div className="reminder-email-copy">
            <div className="section-heading">
              <h3>{t("emailReminder")}</h3>
            </div>
            <p>
              <strong>{t("reminderEmailSubject", { count: 1 })}</strong>
            </p>
            <p className="reminder-email-body">
              {t("reminderEmailText", { count: 1 })}
            </p>
            <button
              className="secondary-button"
              type="button"
              onClick={() =>
                void navigator.clipboard?.writeText(
                  `${t("reminderEmailSubject", { count: 1 })}\n\n${t("reminderEmailText", { count: 1 })}`,
                )
              }
            >
              {t("copyEmail")}
            </button>
          </div>
        </article>
      </section>
      {showOpportunityForm && (
        <div className="dialog-backdrop" role="presentation">
          <section
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-opportunity-title"
          >
            <div className="dialog-title-row">
              <h2 id="new-opportunity-title">{t("addOpportunity")}</h2>
              <button
                className="icon-button"
                type="button"
                onClick={() => setShowOpportunityForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={(event) => void submitOpportunity(event)}>
              <label>
                {t("client")}
                <input
                  value={opportunity.clientDisplayName}
                  onChange={(event) =>
                    setOpportunity({
                      ...opportunity,
                      clientDisplayName: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                {t("market")}
                <input
                  value={opportunity.market}
                  onChange={(event) =>
                    setOpportunity({
                      ...opportunity,
                      market: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                {t("projectName")}
                <input
                  value={opportunity.projectName}
                  onChange={(event) =>
                    setOpportunity({
                      ...opportunity,
                      projectName: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                {t("projectSummary")}
                <textarea
                  value={opportunity.projectSummary}
                  onChange={(event) =>
                    setOpportunity({
                      ...opportunity,
                      projectSummary: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                {t("nextCheckIn")}
                <input
                  type="date"
                  value={opportunity.nextCheckInOn}
                  onChange={(event) =>
                    setOpportunity({
                      ...opportunity,
                      nextCheckInOn: event.target.value,
                    })
                  }
                  required
                />
              </label>
              <div className="dialog-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setShowOpportunityForm(false)}
                >
                  {t("cancel")}
                </button>
                <button className="primary-button" type="submit">
                  {t("save")}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {notice && (
        <div className="toast" role="status">
          ✓ {notice}
        </div>
      )}
      <section className="panel audit-panel">
        <div className="section-heading">
          <h2>{t("audit")}</h2>
        </div>
        <div className="timeline">
          {auditEvents.map((event) => (
            <div className="timeline-item" key={event.id}>
              <span className="timeline-dot" />
              <div>
                <strong>{event.summary}</strong>
                <p>
                  {event.actorName} · {event.action}
                </p>
                <small>
                  {new Intl.DateTimeFormat(locale, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(event.occurredAt))}
                </small>
                {(event.attachments?.length ?? 0) > 0 && (
                  <div className="audit-attachments">
                    {event.attachments?.map((attachment) =>
                      attachment.downloadUrl ? (
                        <a
                          href={attachment.downloadUrl}
                          key={attachment.id}
                          target="_blank"
                          rel="noreferrer"
                        >
                          📎 {attachment.fileName}
                        </a>
                      ) : (
                        <span key={attachment.id}>
                          📎 {attachment.fileName}
                        </span>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
